// ── Payments & Perks section ──────────────────────────────────────────────────
// Bento grid: Pix online payment + Coupons + Cashback

/** Inline illustration: readable QR motif (dark modules + white gaps, not empty). */
function FakeQrTile({ className }) {
  const n = 11

  const cornerCell = (lx, ly) => {
    if (lx >= 0 && lx <= 2 && ly >= 0 && ly <= 2) {
      if (lx === 1 && ly === 1) return false
      return true
    }
    return false
  }

  const bits = []
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (x < 3 && y < 3) {
        bits.push(cornerCell(x, y))
        continue
      }
      if (x >= n - 3 && y < 3) {
        bits.push(cornerCell(x - (n - 3), y))
        continue
      }
      if (x < 3 && y >= n - 3) {
        bits.push(cornerCell(x, y - (n - 3)))
        continue
      }
      bits.push(((x * 7 + y * 13 + x * y) % 11) < 5)
    }
  }

  return (
    <div
      className={`rounded-xl p-2.5 bg-white ring-1 ring-zinc-300/90 shadow-inner ${className || ''}`}
      style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.95)' }}
    >
      <div className="grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {bits.map((on, i) => (
          <div
            key={i}
            className={`aspect-square rounded-[1.5px] ${on ? 'bg-zinc-900' : 'bg-white ring-[0.5px] ring-zinc-200/90'}`}
          />
        ))}
      </div>
    </div>
  )
}

function PixMock() {
  return (
    <div className="flex flex-col h-full">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500 mb-3">
        Pagamento via Pix
      </div>
      <div className="flex-1 flex flex-col rounded-2xl overflow-hidden shadow-[0_12px_40px_-12px_rgba(15,118,110,0.18)] ring-1 ring-teal-900/5">
        <div className="px-4 pt-4 pb-4 sm:px-5 bg-gradient-to-br from-teal-50 via-white to-white">
          <div className="flex flex-col sm:flex-row items-stretch gap-4 sm:gap-5 sm:items-center">
            <div className="flex flex-col items-center sm:items-stretch shrink-0">
              <FakeQrTile className="w-[104px] sm:w-[118px]" />
              <span className="mt-2 text-[8.5px] font-semibold uppercase tracking-wider text-teal-700/90">
                Pix copia e cola
              </span>
            </div>
            <div className="flex-1 flex flex-col justify-center min-w-0 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                <span
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-bold text-white shadow-sm"
                  style={{ background: 'linear-gradient(145deg, #14b8a6 0%, #0f766e 100%)' }}
                >
                  <svg width="12" height="12" viewBox="0 0 22 22" fill="none" aria-hidden className="opacity-95">
                    <path d="M5.5 11L11 5.5L16.5 11L11 16.5L5.5 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  </svg>
                  Pix
                </span>
              </div>
              <div className="text-[24px] sm:text-[26px] font-black tracking-tight text-neutral-900 tabular-nums leading-none">
                R$68,00
              </div>
              <div className="text-[10px] text-neutral-500 mt-1.5">Cantina Bella · #00091</div>
              <div className="mt-3 flex items-center gap-2 bg-emerald-50 px-2.5 py-1.5 rounded-full ring-1 ring-emerald-200/70 self-center sm:self-start">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-40" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[10px] font-semibold text-emerald-800">Aguardando pagamento</span>
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-3.5 bg-neutral-50/90 border-t border-neutral-100">
          <p className="text-center text-[9.5px] text-neutral-500 leading-relaxed">
            Taxa de processamento: máx. R$1 por transação
            <br />
            <span className="text-neutral-400">(~90% para a Woovi · WaitHere retém o mínimo)</span>
          </p>
        </div>
      </div>
    </div>
  )
}

const sampleCoupons = [
  { code: 'BOAS_VINDAS', type: 'percentage', value: 15, uses: 28, limit: 50, active: true },
  { code: 'FRETE10',     type: 'fixed',      value: 10, uses: 41, limit: 100, active: true },
  { code: 'VIP_BRUNA',   type: 'percentage', value: 20, uses: 1, limit: 1, active: false, targeted: true },
]

function CouponsMock() {
  return (
    <div className="flex flex-col h-full">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500 mb-3">
        Cupons de desconto
      </div>
      <div className="flex-1 flex flex-col gap-2.5">
        {sampleCoupons.map((c) => (
          <div
            key={c.code}
            className={`flex items-center gap-3 p-3 rounded-2xl transition-shadow ${
              c.active
                ? 'bg-gradient-to-r from-white to-emerald-50/40 ring-1 ring-emerald-100 shadow-[0_8px_24px_-8px_rgba(16,185,129,0.12)]'
                : 'bg-neutral-50/80 ring-1 ring-neutral-200/70'
            }`}
          >
            <div
              className={`flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[10px] font-black tracking-tight font-mono shadow-sm ${
                c.active
                  ? 'bg-white text-emerald-800 ring-1 ring-emerald-200/80'
                  : 'bg-white/60 text-neutral-500 ring-1 ring-neutral-200'
              }`}
            >
              {c.code}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold text-neutral-900">
                {c.type === 'percentage' ? `${c.value}% off` : `R$${c.value} off`}
                {c.targeted && (
                  <span className="ml-1.5 text-[8.5px] font-semibold px-2 py-0.5 rounded-md bg-violet-100 text-violet-700 ring-1 ring-violet-200/80">
                    Targeted
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1.5 rounded-full bg-neutral-200/80 overflow-hidden shadow-inner">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400"
                    style={{
                      width: `${Math.min((c.uses / c.limit) * 100, 100)}%`,
                      opacity: c.active ? 1 : 0.45,
                    }}
                  />
                </div>
                <span className="text-[9px] text-neutral-500 font-medium tabular-nums flex-shrink-0">
                  {c.uses}/{c.limit}
                </span>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="flex items-center justify-center gap-1.5 p-3 rounded-2xl bg-neutral-100/90 hover:bg-neutral-200/80 transition-colors ring-1 ring-neutral-200/80 text-[11px] font-semibold text-neutral-700"
        >
          <span className="text-neutral-500 text-base leading-none">+</span>
          Novo cupom
        </button>
      </div>
    </div>
  )
}

const cashbackClients = [
  { initials: 'MR', name: 'Marina R.',  balance: 'R$12,00', orders: 8 },
  { initials: 'CS', name: 'Carlos S.',  balance: 'R$28,50', orders: 19 },
  { initials: 'LP', name: 'Luana P.',   balance: 'R$6,00',  orders: 4 },
]

function CashbackMock() {
  return (
    <div className="flex flex-col h-full">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500 mb-3">
        Cashback para clientes
      </div>
      <div className="flex gap-2 mb-4">
        <div className="flex-1 p-2.5 rounded-2xl bg-gradient-to-br from-neutral-100 to-white ring-1 ring-neutral-200/70 text-center shadow-sm">
          <div className="text-[17px] font-black text-neutral-900 tabular-nums">5%</div>
          <div className="text-[9px] text-neutral-500 font-medium">por pedido</div>
        </div>
        <div className="flex-1 p-2.5 rounded-2xl bg-gradient-to-br from-neutral-100 to-white ring-1 ring-neutral-200/70 text-center shadow-sm">
          <div className="text-[17px] font-black text-neutral-900 tabular-nums">R$5</div>
          <div className="text-[9px] text-neutral-500 font-medium">mín. resgate</div>
        </div>
        <div className="flex-1 p-2.5 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50/60 ring-1 ring-emerald-200/60 text-center shadow-sm">
          <div className="text-[17px] font-black bg-gradient-to-br from-emerald-600 to-teal-600 bg-clip-text text-transparent tabular-nums">47</div>
          <div className="text-[9px] text-neutral-600 font-medium">clientes ativos</div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {cashbackClients.map((c) => (
          <div
            key={c.name}
            className="flex items-center gap-3 p-3 rounded-2xl bg-white ring-1 ring-neutral-200/75 shadow-[0_6px_20px_-6px_rgba(0,0,0,0.06)]"
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 bg-gradient-to-br from-neutral-200 to-neutral-300 text-neutral-800 ring-2 ring-white shadow-sm">
              {c.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold text-neutral-900">{c.name}</div>
              <div className="text-[9.5px] text-neutral-500">{c.orders} pedidos</div>
            </div>
            <div className="text-[12px] font-black tabular-nums bg-gradient-to-br from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {c.balance}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const bentoCardClass =
  'rounded-[22px] p-6 sm:p-7 flex flex-col bg-white/90 backdrop-blur-sm ring-1 ring-neutral-200/80 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.12)] min-h-[380px] transition-shadow hover:shadow-[0_24px_56px_-20px_rgba(0,0,0,0.14)]'

export default function PaymentsAndPerks() {
  return (
    <section
      className="px-5 py-16 sm:px-8 sm:py-20 lg:px-16 lg:py-24 relative overflow-hidden"
      style={{
        background: 'radial-gradient(1200px 600px at 50% -10%, rgba(45,212,191,0.08), transparent), radial-gradient(800px 400px at 100% 60%, rgba(167,139,250,0.06), transparent), #FAFAF8',
      }}
    >
      <div className="max-w-[1140px] mx-auto relative z-10">
        <div className="text-[11px] font-bold uppercase tracking-[2px] text-neutral-500 mb-3">
          Pagamentos & Fidelização
        </div>
        <h2 className="text-[28px] sm:text-[36px] lg:text-[46px] font-black tracking-tight leading-[1.08] mb-4 text-neutral-900">
          Venda mais. Fidelize mais.
        </h2>
        <p className="text-[15px] lg:text-[16px] text-neutral-600 leading-[1.75] max-w-[520px] mb-10 lg:mb-14">
          Receba online via Pix, crie cupons de desconto e recompense clientes com cashback; tudo integrado ao seu cardápio.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          <div className={bentoCardClass}>
            <PixMock />
          </div>
          <div className={bentoCardClass}>
            <CouponsMock />
          </div>
          <div className={bentoCardClass}>
            <CashbackMock />
          </div>
        </div>

        <div className="mt-6 rounded-[22px] p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 bg-white/95 backdrop-blur-sm ring-1 ring-neutral-200/80 shadow-[0_16px_48px_-20px_rgba(0,0,0,0.1)]">
          <div className="flex-1">
            <div className="text-[15px] font-bold text-neutral-900 mb-1">Taxa de processamento máxima de R$1 por transação.</div>
            <div className="text-[13px] text-neutral-600 leading-relaxed">
              Pagamentos via Pix com Woovi. Cerca de 90% dessa taxa vai direto para a Woovi como custo de processamento — o WaitHere fica com o mínimo. Cashback e cupons inclusos, sem custo extra.
            </div>
          </div>
          <a
            href="#testar-gratis"
            className="shrink-0 inline-flex items-center justify-center bg-[#141410] text-white no-underline px-6 py-3.5 rounded-full text-[13px] font-bold hover:bg-[#2a2a26] transition-all shadow-lg shadow-neutral-900/15 hover:shadow-xl whitespace-nowrap"
          >
            Começar grátis →
          </a>
        </div>
      </div>
    </section>
  )
}
